import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTenant } from "../../../hooks/useTenant";
import AttendanceService from "../../../services/attendance_service";
import { useAttendanceData } from "./useAttendanceData";
import { useAttendanceSearch } from "./useAttendanceSearch";
import { useAttendanceTeachers } from "./useAttendanceTeachers";

/**
 * useAttendanceModal
 * Orquestrador (Controller) para o modal de controle de presença.
 * O pesado da lógica foi dividido em hooks com responsabilidades únicas.
 */
export const useAttendanceModal = ({ isOpen, onClose, schedule, onAttendanceSaved }) => {
  const navigate = useNavigate();
  const { idTenant, user } = useTenant();

  // 1. Hook especializado nos dados de membros da aula
  const {
      members,
      setMembers,
      loadingMembers,
      gamificationSettings,
      attendanceStats,
      isDirty,
      hasManualChanges,
      handleMarkAbsence,
      handleMarkPresent,
      handleChangeJustification
  } = useAttendanceData({ isOpen, schedule, idTenant });

  // 2. Hook especializado na busca de alunos extras
  const {
      searchText,
      setSearchText,
      searchResults,
      isSearchingExtra,
      justAddedId,
      handleSelectSearchMember
  } = useAttendanceSearch({ isOpen, idTenant, members, setMembers });

  // 3. Hook especializado na troca de professores (substituto)
  const {
      isChangingTeacher,
      setIsChangingTeacher,
      substituteTeacher,
      setSubstituteTeacher,
      availableTeachers,
      isLoadingTeachers
  } = useAttendanceTeachers({ idTenant, isOpen });

  // State local do orquestrador
  const [isSaving, setIsSaving] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // -- Loading por tipo --
  const isLoading = (type) => {
    if (type === "save") return isSaving;
    if (type === "addExtra") return isSearchingExtra;
    return false;
  };

  // -- Ações de Persistência (Salvar / Cancelar) --
  const handleCancelSession = async (cancelReason = "admin") => {
    setIsCanceling(true);
    try {
      await AttendanceService.cancelSession(idTenant, schedule, user, cancelReason);
      if (onAttendanceSaved) {
        await onAttendanceSaved();
      }
      toast.success("Aula cancelada e notificada com sucesso!");
      onClose();
    } catch (error) {
      console.error("[useAttendanceModal] Erro ao cancelar:", error);
      toast.error(error.message || "Erro inesperado ao cancelar aula.");
    } finally {
      setIsCanceling(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const substitutionData = substituteTeacher ? {
        idTeacher: substituteTeacher.id,
        teacherName: substituteTeacher.fullName || substituteTeacher.label
      } : null;

      await AttendanceService.saveSessionAttendance(
          idTenant, schedule, members, user, gamificationSettings, substitutionData
      );
      if (onAttendanceSaved) {
        await onAttendanceSaved();
      }
      toast.success("Lista de presença salva com sucesso!");
      onClose();
    } catch (error) {
      console.error("[useAttendanceModal] Erro ao salvar:", error);
      toast.error(error.message || "Erro inesperado ao salvar controle de presença.");
    } finally {
      setIsSaving(false);
    }
  };

  // -- Navegação / Fechamento --
  const handleClose = () => {
    if (hasManualChanges || !!substituteTeacher) {
      setShowConfirm(true);
    } else {
      onClose();
    }
  };

  const handleForceClose = () => {
    setShowConfirm(false);
    onClose();
  };

  const title = useMemo(() => {
    if (!schedule) return "Controle de presença";
    return `${schedule.activityName} · ${schedule.startTime} - ${schedule.endTime}`;
  }, [schedule]);

  const handleGoToProfile = (idMember) => {
    onClose();
    navigate(`/members/${idMember}`);
  };

  return {
    members,
    loadingMembers,
    searchText,
    searchResults,
    justAddedId,
    attendanceStats,
    isDirty,
    showConfirm,
    title,
    setSearchText,
    setShowConfirm,
    isLoading,
    handleSelectSearchMember,
    handleMarkAbsence,
    handleMarkPresent,
    handleChangeJustification,
    handleSave,
    handleClose,
    handleForceClose,
    handleGoToProfile,
    isChangingTeacher,
    setIsChangingTeacher,
    substituteTeacher,
    setSubstituteTeacher,
    availableTeachers,
    isLoadingTeachers,
    isCanceling,
    handleCancelSession,
  };
};
